import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { apiClient } from "../services/api";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  CalendarDays,
  Plus,
  RefreshCw,
  Users,
  TrendingUp,
  ClipboardList,
  History,
  Edit2,
  Building2,
  User,
} from "lucide-react";

const TABS = [
  { id: "politiques", icon: ClipboardList },
  { id: "affectations", icon: Users },
  { id: "soldes", icon: TrendingUp },
  { id: "historique", icon: History },
];

const LEAVE_TYPES = ["vacation", "sick", "maternity", "paternity", "unpaid", "bereavement", "other"];
const ACCRUAL_METHODS = ["none", "monthly", "bi_weekly", "annual_lump"];

const LEAVE_TYPE_COLORS = {
  vacation: "bg-blue-100 text-blue-800",
  sick: "bg-red-100 text-red-800",
  maternity: "bg-pink-100 text-pink-800",
  paternity: "bg-purple-100 text-purple-800",
  unpaid: "bg-gray-100 text-gray-800",
  bereavement: "bg-slate-100 text-slate-800",
  other: "bg-yellow-100 text-yellow-800",
};

const leaveTypeLabel = (type, t) => t(`leavePolicyHub.leaveTypes.${type}`, { defaultValue: type });
const accrualMethodLabel = (method, t) => t(`leavePolicyHub.accrualMethods.${method}`, { defaultValue: method });
const reasonLabel = (reason, t) => t(`leavePolicyHub.reasons.${reason}`, { defaultValue: reason });

// ── Politiques ─────────────────────────────────────────────────────────────────

function PolitiquesTab() {
  const { t } = useTranslation();
  const [policies, setPolicies] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingPolicy, setEditingPolicy] = useState(null);
  const [form, setForm] = useState({
    name: "", leave_type: "vacation", accrual_method: "monthly",
    accrual_amount: 2, max_balance: "", carryover_days: 0,
    negative_balance_allowed: false, waiting_period_days: 0,
    requires_approval: true, description: "",
  });

  const load = async () => {
    setLoading(true);
    try {
      const [p, s] = await Promise.all([
        apiClient.get("v1/leave-policies?active_only=false"),
        apiClient.get("v1/leave-policies/stats"),
      ]);
      setPolicies(p.data);
      setStats(s.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleSubmit = async () => {
    const payload = {
      ...form,
      accrual_amount: parseFloat(form.accrual_amount) || 0,
      max_balance: form.max_balance !== "" ? parseFloat(form.max_balance) : null,
      carryover_days: parseFloat(form.carryover_days) || 0,
      waiting_period_days: parseInt(form.waiting_period_days) || 0,
    };
    if (editingPolicy) {
      await apiClient.patch(`v1/leave-policies/${editingPolicy.id}`, payload);
    } else {
      await apiClient.post("v1/leave-policies", payload);
    }
    setShowForm(false);
    setEditingPolicy(null);
    setForm({ name: "", leave_type: "vacation", accrual_method: "monthly", accrual_amount: 2, max_balance: "", carryover_days: 0, negative_balance_allowed: false, waiting_period_days: 0, requires_approval: true, description: "" });
    load();
  };

  const openEdit = (p) => {
    setEditingPolicy(p);
    setForm({
      name: p.name, leave_type: p.leave_type, accrual_method: p.accrual_method,
      accrual_amount: p.accrual_amount, max_balance: p.max_balance ?? "",
      carryover_days: p.carryover_days, negative_balance_allowed: p.negative_balance_allowed,
      waiting_period_days: p.waiting_period_days, requires_approval: p.requires_approval,
      description: p.description || "",
    });
    setShowForm(true);
  };

  if (loading) return <div className="flex items-center justify-center h-64 text-gray-500">{t("leavePolicyHub.loading")}</div>;

  return (
    <div className="space-y-6">
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { label: t("leavePolicyHub.politiques.statPolicies"), value: stats.total_policies, color: "text-blue-600" },
            { label: t("leavePolicyHub.politiques.statAssignments"), value: stats.total_assignments, color: "text-purple-600" },
            { label: t("leavePolicyHub.politiques.statCovered"), value: stats.employees_covered, color: "text-green-600" },
            { label: t("leavePolicyHub.politiques.statLogs"), value: stats.total_logs, color: "text-orange-600" },
          ].map((s) => (
            <div key={s.label} className="bg-white rounded-xl border p-4 text-center">
              <div className={`text-2xl font-bold ${s.color}`}>{s.value}</div>
              <div className="text-xs text-gray-500 mt-1">{s.label}</div>
            </div>
          ))}
        </div>
      )}

      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-gray-800">{t("leavePolicyHub.politiques.title")}</h3>
        <Button size="sm" onClick={() => { setEditingPolicy(null); setShowForm(true); }}>
          <Plus className="h-4 w-4 mr-1" /> {t("leavePolicyHub.politiques.new")}
        </Button>
      </div>

      {showForm && (
        <div className="bg-white rounded-xl border p-6 space-y-4">
          <h4 className="font-medium text-gray-800">{editingPolicy ? t("leavePolicyHub.politiques.editTitle") : t("leavePolicyHub.politiques.newTitle")}</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">{t("leavePolicyHub.politiques.name")}</label>
              <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder={t("leavePolicyHub.politiques.namePlaceholder")} />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">{t("leavePolicyHub.politiques.leaveType")}</label>
              <Select value={form.leave_type} onValueChange={v => setForm(f => ({ ...f, leave_type: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {LEAVE_TYPES.map((type) => <SelectItem key={type} value={type}>{leaveTypeLabel(type, t)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">{t("leavePolicyHub.politiques.accrualMethod")}</label>
              <Select value={form.accrual_method} onValueChange={v => setForm(f => ({ ...f, accrual_method: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ACCRUAL_METHODS.map((method) => <SelectItem key={method} value={method}>{accrualMethodLabel(method, t)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">{t("leavePolicyHub.politiques.daysPerPeriod")}</label>
              <Input type="number" step="0.5" value={form.accrual_amount} onChange={e => setForm(f => ({ ...f, accrual_amount: e.target.value }))} />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">{t("leavePolicyHub.politiques.maxBalance")}</label>
              <Input type="number" step="0.5" value={form.max_balance} onChange={e => setForm(f => ({ ...f, max_balance: e.target.value }))} placeholder={t("leavePolicyHub.politiques.maxBalancePlaceholder")} />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">{t("leavePolicyHub.politiques.carryover")}</label>
              <Input type="number" step="0.5" value={form.carryover_days} onChange={e => setForm(f => ({ ...f, carryover_days: e.target.value }))} />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">{t("leavePolicyHub.politiques.waitingPeriod")}</label>
              <Input type="number" value={form.waiting_period_days} onChange={e => setForm(f => ({ ...f, waiting_period_days: e.target.value }))} />
            </div>
            <div className="flex items-center gap-4 pt-4">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={form.requires_approval} onChange={e => setForm(f => ({ ...f, requires_approval: e.target.checked }))} />
                {t("leavePolicyHub.politiques.requiresApproval")}
              </label>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={form.negative_balance_allowed} onChange={e => setForm(f => ({ ...f, negative_balance_allowed: e.target.checked }))} />
                {t("leavePolicyHub.politiques.negativeAllowed")}
              </label>
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">{t("leavePolicyHub.politiques.description")}</label>
            <Textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} rows={2} />
          </div>
          <div className="flex gap-2">
            <Button onClick={handleSubmit}>{editingPolicy ? t("leavePolicyHub.politiques.save") : t("leavePolicyHub.politiques.create")}</Button>
            <Button variant="outline" onClick={() => { setShowForm(false); setEditingPolicy(null); }}>{t("leavePolicyHub.politiques.cancel")}</Button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {policies.map((p) => (
          <div key={p.id} className="bg-white rounded-xl border p-5 hover:shadow-md transition-shadow">
            <div className="flex items-start justify-between mb-3">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${LEAVE_TYPE_COLORS[p.leave_type]}`}>
                    {leaveTypeLabel(p.leave_type, t)}
                  </span>
                  {!p.is_active && <Badge variant="outline" className="text-xs">{t("leavePolicyHub.politiques.inactive")}</Badge>}
                </div>
                <h4 className="font-semibold text-gray-900">{p.name}</h4>
                {p.description && <p className="text-xs text-gray-500 mt-0.5">{p.description}</p>}
              </div>
              <Button variant="ghost" size="icon" onClick={() => openEdit(p)}>
                <Edit2 className="h-4 w-4" />
              </Button>
            </div>
            <div className="grid grid-cols-2 gap-2 text-xs text-gray-600">
              <div><span className="font-medium">{t("leavePolicyHub.politiques.accrualLabel")}</span> {accrualMethodLabel(p.accrual_method, t)} - {p.accrual_amount}j</div>
              <div><span className="font-medium">{t("leavePolicyHub.politiques.maxLabel")}</span> {p.max_balance ?? t("leavePolicyHub.politiques.unlimited")}</div>
              <div><span className="font-medium">{t("leavePolicyHub.politiques.carryoverLabel")}</span> {p.carryover_days}j</div>
              <div><span className="font-medium">{t("leavePolicyHub.politiques.approvalLabel")}</span> {p.requires_approval ? t("leavePolicyHub.politiques.yes") : t("leavePolicyHub.politiques.no")}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Affectations ───────────────────────────────────────────────────────────────

function AffectationsTab() {
  const { t } = useTranslation();
  const [policies, setPolicies] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [selectedPolicy, setSelectedPolicy] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ scope: "company", employee_id: "", department_id: "", effective_from: new Date().toISOString().split("T")[0], effective_to: "" });

  useEffect(() => {
    Promise.all([
      apiClient.get("v1/leave-policies?active_only=true"),
      apiClient.get("v1/employees/"),
      apiClient.get("v1/organization-chart/departments/"),
    ]).then(([p, e, d]) => {
      setPolicies(p.data);
      setEmployees(e.data.employees || e.data || []);
      setDepartments(d.data || []);
      if (p.data.length > 0) {
        setSelectedPolicy(p.data[0].id);
        loadAssignments(p.data[0].id);
      }
    }).finally(() => setLoading(false));
  }, []);

  const loadAssignments = async (policyId) => {
    const r = await apiClient.get(`v1/leave-policies/${policyId}/assignments`);
    setAssignments(r.data);
  };

  const handlePolicySelect = (id) => {
    setSelectedPolicy(id);
    loadAssignments(id);
  };

  const handleAssign = async () => {
    const payload = {
      scope: form.scope,
      effective_from: form.effective_from,
    };
    if (form.scope === "employee" && form.employee_id) payload.employee_id = parseInt(form.employee_id);
    if (form.scope === "department" && form.department_id) payload.department_id = parseInt(form.department_id);
    if (form.effective_to) payload.effective_to = form.effective_to;
    await apiClient.post(`v1/leave-policies/${selectedPolicy}/assignments`, payload);
    setShowForm(false);
    loadAssignments(selectedPolicy);
  };

  if (loading) return <div className="flex items-center justify-center h-64 text-gray-500">{t("leavePolicyHub.loading")}</div>;

  return (
    <div className="space-y-6">
      <div className="flex gap-2 flex-wrap">
        {policies.map((p) => (
          <button
            key={p.id}
            onClick={() => handlePolicySelect(p.id)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${selectedPolicy === p.id ? "bg-blue-600 text-white" : "bg-gray-100 text-gray-700 hover:bg-gray-200"}`}
          >
            {p.name}
          </button>
        ))}
      </div>

      {selectedPolicy && (
        <>
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-gray-800">{t("leavePolicyHub.affectations.title", { count: assignments.length })}</h3>
            <Button size="sm" onClick={() => setShowForm(true)}>
              <Plus className="h-4 w-4 mr-1" /> {t("leavePolicyHub.affectations.assign")}
            </Button>
          </div>

          {showForm && (
            <div className="bg-white rounded-xl border p-5 space-y-4">
              <h4 className="font-medium">{t("leavePolicyHub.affectations.newTitle")}</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">{t("leavePolicyHub.affectations.scope")}</label>
                  <Select value={form.scope} onValueChange={v => setForm(f => ({ ...f, scope: v }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="company">{t("leavePolicyHub.affectations.scopeCompany")}</SelectItem>
                      <SelectItem value="department">{t("leavePolicyHub.affectations.scopeDepartment")}</SelectItem>
                      <SelectItem value="employee">{t("leavePolicyHub.affectations.scopeEmployee")}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {form.scope === "employee" && (
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">{t("leavePolicyHub.affectations.employee")}</label>
                    <Select value={form.employee_id} onValueChange={v => setForm(f => ({ ...f, employee_id: v }))}>
                      <SelectTrigger><SelectValue placeholder={t("leavePolicyHub.affectations.select")} /></SelectTrigger>
                      <SelectContent>
                        {employees.map(e => <SelectItem key={e.id} value={String(e.id)}>{e.first_name} {e.last_name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                {form.scope === "department" && (
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">{t("leavePolicyHub.affectations.department")}</label>
                    <Select value={form.department_id} onValueChange={v => setForm(f => ({ ...f, department_id: v }))}>
                      <SelectTrigger><SelectValue placeholder={t("leavePolicyHub.affectations.select")} /></SelectTrigger>
                      <SelectContent>
                        {departments.map(d => <SelectItem key={d.id} value={String(d.id)}>{d.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">{t("leavePolicyHub.affectations.effectiveFrom")}</label>
                  <Input type="date" value={form.effective_from} onChange={e => setForm(f => ({ ...f, effective_from: e.target.value }))} />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">{t("leavePolicyHub.affectations.effectiveTo")}</label>
                  <Input type="date" value={form.effective_to} onChange={e => setForm(f => ({ ...f, effective_to: e.target.value }))} />
                </div>
              </div>
              <div className="flex gap-2">
                <Button onClick={handleAssign}>{t("leavePolicyHub.affectations.assign")}</Button>
                <Button variant="outline" onClick={() => setShowForm(false)}>{t("leavePolicyHub.politiques.cancel")}</Button>
              </div>
            </div>
          )}

          <div className="space-y-3">
            {assignments.length === 0 && (
              <div className="text-center py-12 text-gray-400">{t("leavePolicyHub.affectations.empty")}</div>
            )}
            {assignments.map((a) => (
              <div key={a.id} className="bg-white rounded-xl border p-4 flex items-center gap-4">
                <div className="p-2 rounded-lg bg-gray-50">
                  {a.scope === "company" ? <Building2 className="h-5 w-5 text-blue-500" /> :
                   a.scope === "department" ? <Building2 className="h-5 w-5 text-purple-500" /> :
                   <User className="h-5 w-5 text-green-500" />}
                </div>
                <div className="flex-1">
                  <div className="font-medium text-gray-800 text-sm">
                    {a.scope === "company" ? t("leavePolicyHub.affectations.scopeCompany") :
                     a.scope === "department" && a.department ? a.department.name :
                     a.employee ? `${a.employee.first_name} ${a.employee.last_name}` : "—"}
                  </div>
                  <div className="text-xs text-gray-500">
                    {t("leavePolicyHub.affectations.sinceLabel", { from: a.effective_from })} {a.effective_to ? `-> ${a.effective_to}` : t("leavePolicyHub.affectations.open")}
                  </div>
                </div>
                <Badge variant="outline" className="text-xs capitalize">{a.scope}</Badge>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ── Soldes ─────────────────────────────────────────────────────────────────────

function SoldesTab() {
  const { t } = useTranslation();
  const [allBalances, setAllBalances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [runningAccrual, setRunningAccrual] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const all = await apiClient.get("v1/leave-policies/balances/all");
      setAllBalances(all.data);
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const runAccrual = async () => {
    setRunningAccrual(true);
    try {
      const r = await apiClient.post("v1/leave-policies/accrual/run", {});
      alert(t("leavePolicyHub.soldes.accrualResult", { accrued: r.data.accrued, skipped: r.data.skipped }));
      load();
    } finally {
      setRunningAccrual(false);
    }
  };

  const getBalanceColor = (balance, maxBalance) => {
    if (maxBalance && balance / maxBalance > 0.8) return "text-green-600";
    if (balance < 0) return "text-red-600";
    if (balance < 3) return "text-orange-500";
    return "text-blue-600";
  };

  if (loading) return <div className="flex items-center justify-center h-64 text-gray-500">{t("leavePolicyHub.loading")}</div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div />
        <Button size="sm" variant="outline" onClick={runAccrual} disabled={runningAccrual}>
          <RefreshCw className={`h-4 w-4 mr-1 ${runningAccrual ? "animate-spin" : ""}`} />
          {t("leavePolicyHub.soldes.runAccrual")}
        </Button>
      </div>

      <div className="space-y-3">
          {allBalances.length === 0 && (
            <div className="text-center py-12 text-gray-400">{t("leavePolicyHub.soldes.empty")}</div>
          )}
          {allBalances.map((emp) => (
            <div key={emp.employee_id} className="bg-white rounded-xl border p-4">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <div className="font-semibold text-gray-800 text-sm">{emp.employee_name}</div>
                  {emp.department && <div className="text-xs text-gray-500">{emp.department}</div>}
                </div>
                <Badge variant="outline" className="text-xs">{t("leavePolicyHub.soldes.policiesCount", { count: emp.balances.length })}</Badge>
              </div>
              <div className="flex flex-wrap gap-2">
                {emp.balances.map((b) => (
                  <div key={b.policy_id} className="flex items-center gap-1.5 bg-gray-50 rounded-lg px-3 py-1.5">
                    <span className={`text-xs px-1.5 py-0.5 rounded ${LEAVE_TYPE_COLORS[b.leave_type]}`}>
                      {leaveTypeLabel(b.leave_type, t)}
                    </span>
                    <span className={`text-sm font-bold ${getBalanceColor(b.balance, b.max_balance)}`}>
                      {b.balance.toFixed(1)}j
                    </span>
                  </div>
                ))}
                {emp.balances.length === 0 && <span className="text-xs text-gray-400">{t("leavePolicyHub.soldes.noPolicies")}</span>}
              </div>
            </div>
          ))}
      </div>
    </div>
  );
}

// ── Historique ─────────────────────────────────────────────────────────────────

const REASON_COLORS = {
  monthly_accrual: "bg-green-100 text-green-800",
  manual: "bg-blue-100 text-blue-800",
  adjustment: "bg-yellow-100 text-yellow-800",
  carryover: "bg-purple-100 text-purple-800",
  usage: "bg-red-100 text-red-800",
  expiry: "bg-gray-100 text-gray-800",
};

function HistoriqueTab() {
  const { t } = useTranslation();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adjustForm, setAdjustForm] = useState({ employee_id: "", policy_id: "", days: 0, reason: "manual", note: "" });
  const [showAdjust, setShowAdjust] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [policies, setPolicies] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const [l, e, p] = await Promise.all([
        apiClient.get("v1/leave-policies/accrual/log?limit=100"),
        apiClient.get("v1/employees/"),
        apiClient.get("v1/leave-policies?active_only=false"),
      ]);
      setLogs(l.data);
      setEmployees(e.data.employees || e.data || []);
      setPolicies(p.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleAdjust = async () => {
    await apiClient.post("v1/leave-policies/accrual/adjust", {
      employee_id: parseInt(adjustForm.employee_id),
      policy_id: parseInt(adjustForm.policy_id),
      days: parseFloat(adjustForm.days),
      reason: adjustForm.reason,
      note: adjustForm.note || null,
    });
    setShowAdjust(false);
    load();
  };

  if (loading) return <div className="flex items-center justify-center h-64 text-gray-500">{t("leavePolicyHub.loading")}</div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-gray-800">{t("leavePolicyHub.historique.title", { count: logs.length })}</h3>
        <Button size="sm" onClick={() => setShowAdjust(true)}>
          <Plus className="h-4 w-4 mr-1" /> {t("leavePolicyHub.historique.manualAdjust")}
        </Button>
      </div>

      {showAdjust && (
        <div className="bg-white rounded-xl border p-5 space-y-4">
          <h4 className="font-medium">{t("leavePolicyHub.historique.adjustTitle")}</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">{t("leavePolicyHub.historique.employee")}</label>
              <Select value={adjustForm.employee_id} onValueChange={v => setAdjustForm(f => ({ ...f, employee_id: v }))}>
                <SelectTrigger><SelectValue placeholder={t("leavePolicyHub.historique.select")} /></SelectTrigger>
                <SelectContent>
                  {employees.map(e => <SelectItem key={e.id} value={String(e.id)}>{e.first_name} {e.last_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">{t("leavePolicyHub.historique.policy")}</label>
              <Select value={adjustForm.policy_id} onValueChange={v => setAdjustForm(f => ({ ...f, policy_id: v }))}>
                <SelectTrigger><SelectValue placeholder={t("leavePolicyHub.historique.select")} /></SelectTrigger>
                <SelectContent>
                  {policies.map(p => <SelectItem key={p.id} value={String(p.id)}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">{t("leavePolicyHub.historique.days")}</label>
              <Input type="number" step="0.5" value={adjustForm.days} onChange={e => setAdjustForm(f => ({ ...f, days: e.target.value }))} />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">{t("leavePolicyHub.historique.reason")}</label>
              <Select value={adjustForm.reason} onValueChange={v => setAdjustForm(f => ({ ...f, reason: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="manual">{t("leavePolicyHub.historique.reasonManual")}</SelectItem>
                  <SelectItem value="adjustment">{t("leavePolicyHub.historique.reasonCorrection")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs font-medium text-gray-600 mb-1">{t("leavePolicyHub.historique.note")}</label>
              <Input value={adjustForm.note} onChange={e => setAdjustForm(f => ({ ...f, note: e.target.value }))} placeholder={t("leavePolicyHub.historique.notePlaceholder")} />
            </div>
          </div>
          <div className="flex gap-2">
            <Button onClick={handleAdjust}>{t("leavePolicyHub.historique.apply")}</Button>
            <Button variant="outline" onClick={() => setShowAdjust(false)}>{t("leavePolicyHub.politiques.cancel")}</Button>
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl border overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b">
            <tr>
              <th className="text-left py-3 px-4 font-medium text-gray-600">{t("leavePolicyHub.historique.colDate")}</th>
              <th className="text-left py-3 px-4 font-medium text-gray-600">{t("leavePolicyHub.historique.colEmployee")}</th>
              <th className="text-left py-3 px-4 font-medium text-gray-600">{t("leavePolicyHub.historique.colPolicy")}</th>
              <th className="text-left py-3 px-4 font-medium text-gray-600">{t("leavePolicyHub.historique.colReason")}</th>
              <th className="text-right py-3 px-4 font-medium text-gray-600">{t("leavePolicyHub.historique.colChange")}</th>
              <th className="text-right py-3 px-4 font-medium text-gray-600">{t("leavePolicyHub.historique.colBalanceAfter")}</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {logs.length === 0 && (
              <tr><td colSpan={6} className="text-center py-12 text-gray-400">{t("leavePolicyHub.historique.empty")}</td></tr>
            )}
            {logs.map((l) => (
              <tr key={l.id} className="hover:bg-gray-50">
                <td className="py-2.5 px-4 text-gray-600 text-xs">{l.accrual_date}</td>
                <td className="py-2.5 px-4 font-medium text-gray-800">
                  {l.employee ? `${l.employee.first_name} ${l.employee.last_name}` : `#${l.employee_id}`}
                </td>
                <td className="py-2.5 px-4 text-gray-600 text-xs">{l.policy?.name ?? `#${l.policy_id}`}</td>
                <td className="py-2.5 px-4">
                  <span className={`text-xs px-2 py-0.5 rounded-full ${REASON_COLORS[l.reason] || "bg-gray-100 text-gray-700"}`}>
                    {reasonLabel(l.reason, t)}
                  </span>
                  {l.note && <div className="text-xs text-gray-400 mt-0.5 max-w-xs truncate">{l.note}</div>}
                </td>
                <td className={`py-2.5 px-4 text-right font-bold ${parseFloat(l.days_changed) >= 0 ? "text-green-600" : "text-red-500"}`}>
                  {parseFloat(l.days_changed) >= 0 ? "+" : ""}{parseFloat(l.days_changed).toFixed(1)}j
                </td>
                <td className="py-2.5 px-4 text-right text-gray-700 font-medium">
                  {parseFloat(l.balance_after).toFixed(1)}j
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Hub ────────────────────────────────────────────────────────────────────────

export default function LeavePolicyHub() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") || "politiques";

  const setTab = (id) => setSearchParams({ tab: id });

  const renderContent = () => {
    switch (activeTab) {
      case "politiques": return <PolitiquesTab />;
      case "affectations": return <AffectationsTab />;
      case "soldes": return <SoldesTab />;
      case "historique": return <HistoriqueTab />;
      default: return <PolitiquesTab />;
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center gap-3">
        <div className="p-2 rounded-xl bg-teal-50">
          <CalendarDays className="h-6 w-6 text-teal-600" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t("leavePolicyHub.title")}</h1>
          <p className="text-sm text-gray-500">{t("leavePolicyHub.subtitle")}</p>
        </div>
      </div>

      <div className="flex gap-1 border-b">
        {TABS.map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.id
                  ? "border-teal-600 text-teal-600"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              <Icon className="h-4 w-4" />
              {t(`leavePolicyHub.tabs.${tab.id}`)}
            </button>
          );
        })}
      </div>

      <div>{renderContent()}</div>
    </div>
  );
}
